import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Activity, Loader2, User, Shield } from "lucide-react";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginActivity = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const res = await fetch(`${API}/admin/login-activity`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setActivities(data.data.activities || []);
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchActivity();
  }, [token]);

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  // Group by date
  const grouped = {};
  activities.forEach(a => {
    const day = a.logged_in_at ? a.logged_in_at.substring(0, 10) : 'unknown';
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(a);
  });

  const formatDayLabel = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      const today = new Date().toISOString().substring(0, 10);
      const yesterday = new Date(Date.now() - 86400000).toISOString().substring(0, 10);
      if (dateStr === today) return 'Today';
      if (dateStr === yesterday) return 'Yesterday';
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-white">Login Activity</h1>
              <p className="text-xs text-zinc-500">Last 3 months</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-zinc-500 text-xs">
            <Activity className="w-4 h-4" />
            {activities.length} logins
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 text-zinc-500 animate-spin mx-auto" />
            </div>
          ) : activities.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">No login activity found</div>
          ) : (
            <div>
              {Object.entries(grouped).map(([day, dayActivities]) => (
                <div key={day}>
                  <div className="px-4 py-2 bg-zinc-800/50 border-b border-zinc-800">
                    <span className="text-xs font-medium text-zinc-400">{formatDayLabel(day)}</span>
                    <span className="text-xs text-zinc-600 ml-2">({dayActivities.length})</span>
                  </div>
                  <div className="divide-y divide-zinc-800/50">
                    {dayActivities.map((a, i) => (
                      <div key={`${day}-${i}`} className="flex items-center justify-between px-4 py-3" data-testid={`activity-row-${day}-${i}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${a.role === 'admin' ? 'bg-amber-600/20' : 'bg-zinc-700/50'}`}>
                            {a.role === 'admin' ? <Shield className="w-4 h-4 text-amber-400" /> : <User className="w-4 h-4 text-zinc-400" />}
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{a.name || a.email}</p>
                            <p className="text-zinc-500 text-xs">{a.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-zinc-400 font-mono">
                            {a.logged_in_at ? new Date(a.logged_in_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </span>
                          <span className={`block text-[10px] px-1.5 py-0.5 rounded mt-0.5 ${a.role === 'admin' ? 'bg-amber-900/30 text-amber-400' : 'bg-zinc-800 text-zinc-500'}`}>
                            {a.role}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginActivity;
